import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Button,
  CardActions,
  CircularProgress,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Typography,
  Paper,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";

const ACCOUNT_NUMBER_LENDERS = [
  "HSBC",
  "Barclays",
  "Yorkshire Building Society",
  "First Direct",
  "Coventry Building Society",
  "Accord Mortgages",
];

const SORT_CODE_LENDERS = ["HSBC", "First Direct"];

const PAYMENT_REFERENCE_LENDERS = [
  "TSB",
  "Lloyds",
  "RBS",
  "Virgin Money",
  "Santander",
  "Halifax",
  "Natwest",
  "Nationwide",
];

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column" as const,
    minHeight: "60vh",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    padding: "2em 1em",
  },
  paper: {
    padding: "2em",
    maxWidth: 650,
    width: "100%",
    borderRadius: 12,
  },
  title: {
    fontWeight: 600,
    marginBottom: "0.5em",
    textAlign: "center" as const,
  },
  subtitle: {
    color: "#666",
    marginBottom: "1.5em",
    textAlign: "center" as const,
    fontSize: "0.9em",
  },
  formControl: {
    marginTop: "1em",
    minWidth: "100%",
  },
  textField: {
    marginTop: "1em",
  },
  actions: {
    padding: "1.5em 0 0 0",
    justifyContent: "center",
  },
  divider: {
    margin: "1.5em 0",
  },
  sectionTitle: {
    fontWeight: 500,
    marginTop: "0.5em",
    color: "#333",
  },
  conditionalNote: {
    marginTop: "0.5em",
    padding: "0.75em 1em",
    backgroundColor: "#e3f2fd",
    border: "1px solid #90caf9",
    borderRadius: 4,
    color: "#1565c0",
    fontSize: "0.85em",
  },
  successBox: {
    marginTop: "1.5em",
    padding: "1em",
    backgroundColor: "#e8f5e9",
    border: "1px solid #66bb6a",
    borderRadius: 4,
    color: "#2e7d32",
    textAlign: "center" as const,
  },
}));

const HybridMortgageUpdate = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [lenders, setLenders] = useState<string[]>([]);
  const [loadingLenders, setLoadingLenders] = useState(false);

  const [email, setEmail] = useState("");
  const [lender, setLender] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [sortCode, setSortCode] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [startDate, setStartDate] = useState("");
  const [currentBalanceAmount, setCurrentBalanceAmount] = useState("");
  const [fixedPaymentCount, setFixedPaymentCount] = useState("");
  const [fixedPaymentAmount, setFixedPaymentAmount] = useState("");
  const [isJointMortgage, setIsJointMortgage] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});

  const needsAccountNumber = ACCOUNT_NUMBER_LENDERS.includes(lender);
  const needsSortCode = SORT_CODE_LENDERS.includes(lender);
  const needsPaymentReference = PAYMENT_REFERENCE_LENDERS.includes(lender);

  const fetchLenders = useCallback(async () => {
    setLoadingLenders(true);
    try {
      const config = getConfigGet({});
      const res: any = await axios.get(ENDPOINTS.CREATE_CREDIT_REPORT_MORTGAGE, config);
      const data = res.data;
      if (data?.status && Array.isArray(data.lenders)) {
        setLenders(data.lenders);
      } else {
        notify("Failed to load lenders", "warning");
      }
    } catch (err: any) {
      notify(
        err?.response?.data?.message || "Error fetching lenders",
        "error"
      );
    } finally {
      setLoadingLenders(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchLenders();
  }, [fetchLenders]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!lender) {
      newErrors.lender = "Lender is required";
    }

    if (needsAccountNumber && !accountNumber.trim()) {
      newErrors.accountNumber = `Account number is required for ${lender}`;
    }

    if (needsSortCode && !sortCode.trim()) {
      newErrors.sortCode = `Sort code is required for ${lender}`;
    }

    if (needsPaymentReference && !paymentReference.trim()) {
      newErrors.paymentReference = `Payment reference is required for ${lender}`;
    }

    if (!startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!currentBalanceAmount) {
      newErrors.currentBalanceAmount = "Current balance is required";
    } else if (
      isNaN(Number(currentBalanceAmount)) ||
      Number(currentBalanceAmount) < 1
    ) {
      newErrors.currentBalanceAmount =
        "Current balance must be a positive number (min 1)";
    }

    if (!fixedPaymentCount) {
      newErrors.fixedPaymentCount = "Remaining term is required";
    } else if (
      isNaN(Number(fixedPaymentCount)) ||
      Number(fixedPaymentCount) < 1
    ) {
      newErrors.fixedPaymentCount =
        "Remaining term must be a positive number";
    }

    if (!fixedPaymentAmount) {
      newErrors.fixedPaymentAmount = "Monthly payment is required";
    } else if (
      isNaN(Number(fixedPaymentAmount)) ||
      Number(fixedPaymentAmount) < 1
    ) {
      newErrors.fixedPaymentAmount =
        "Monthly payment must be a positive number (min 1)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setAccountNumber("");
    setSortCode("");
    setPaymentReference("");
    setStartDate("");
    setCurrentBalanceAmount("");
    setFixedPaymentCount("");
    setFixedPaymentAmount("");
    setIsJointMortgage(false);
    setErrors({});
  };

  const handleLenderChange = (value: string) => {
    setLender(value);
    setAccountNumber("");
    setSortCode("");
    setPaymentReference("");
    setErrors((prev) => {
      const { accountNumber, sortCode, paymentReference, lender, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async () => {
    setSuccessMessage("");

    if (!validate()) {
      notify("Please fix the validation errors", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const payload: Record<string, any> = {
        email: email.trim(),
        lender,
        start_date: startDate,
        current_balance_amount: Number(currentBalanceAmount),
        fixed_payment_count: Number(fixedPaymentCount),
        fixed_payment_amount: Number(fixedPaymentAmount),
        is_joint_mortgage: isJointMortgage,
      };

      if (needsAccountNumber) {
        payload.account_number = accountNumber.trim();
      }
      if (needsSortCode) {
        payload.sort_code = sortCode.trim();
      }
      if (needsPaymentReference) {
        payload.payment_reference = paymentReference.trim();
      }

      const headers = getConfigPost();
      const res: any = await axios.post(
        ENDPOINTS.CREATE_CREDIT_REPORT_MORTGAGE,
        payload,
        { headers }
      );

      if (res.data?.status) {
        setSuccessMessage(
          res.data.message || "Credit report mortgage created/updated successfully"
        );
        notify("Mortgage updated successfully", "success");
        resetForm();
      } else {
        notify(
          res.data?.message || "Unexpected response from server",
          "warning"
        );
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0]?.message ||
        "Error creating/updating mortgage";
      notify(msg, "error", "", undefined, 10000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.main}>
      <Paper className={classes.paper} elevation={3}>
        <Typography variant="h5" className={classes.title}>
          Hybrid Mortgage Update
        </Typography>
        <Typography className={classes.subtitle}>
          Create or update a credit report mortgage entry for a user
        </Typography>

        <Divider className={classes.divider} />

        <Typography variant="subtitle1" className={classes.sectionTitle}>
          User Details
        </Typography>

        <TextField
          className={classes.textField}
          label="Email"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
          }}
          fullWidth
          required
          error={!!errors.email}
          helperText={errors.email}
        />

        <Divider className={classes.divider} />

        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Lender Information
        </Typography>

        <FormControl
          className={classes.formControl}
          required
          error={!!errors.lender}
        >
          <InputLabel id="lender-select-label">Lender</InputLabel>
          <Select
            labelId="lender-select-label"
            value={lender}
            onChange={(e) => handleLenderChange(e.target.value as string)}
            disabled={loadingLenders}
          >
            {loadingLenders ? (
              <MenuItem value="" disabled>
                Loading lenders...
              </MenuItem>
            ) : (
              lenders.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))
            )}
          </Select>
          {errors.lender && (
            <Typography variant="caption" color="error" style={{ marginTop: 4 }}>
              {errors.lender}
            </Typography>
          )}
        </FormControl>

        {lender && (
          <>
            {(needsAccountNumber || needsSortCode || needsPaymentReference) && (
              <div className={classes.conditionalNote}>
                {needsAccountNumber && <span>Account number is required for {lender}. </span>}
                {needsSortCode && <span>Sort code is required for {lender}. </span>}
                {needsPaymentReference && <span>Payment reference is required for {lender}. </span>}
              </div>
            )}

            <TextField
              className={classes.textField}
              label="Account Number"
              value={accountNumber}
              onChange={(e) => {
                setAccountNumber(e.target.value);
                if (errors.accountNumber)
                  setErrors((prev) => ({ ...prev, accountNumber: "" }));
              }}
              fullWidth
              required={needsAccountNumber}
              disabled={!needsAccountNumber}
              error={!!errors.accountNumber}
              helperText={
                errors.accountNumber ||
                (!needsAccountNumber ? "Not applicable for " + lender : "")
              }
            />

            <TextField
              className={classes.textField}
              label="Sort Code"
              value={sortCode}
              onChange={(e) => {
                setSortCode(e.target.value);
                if (errors.sortCode)
                  setErrors((prev) => ({ ...prev, sortCode: "" }));
              }}
              fullWidth
              required={needsSortCode}
              disabled={!needsSortCode}
              error={!!errors.sortCode}
              helperText={
                errors.sortCode ||
                (!needsSortCode ? "Not applicable for " + lender : "")
              }
            />

            <TextField
              className={classes.textField}
              label="Payment Reference"
              value={paymentReference}
              onChange={(e) => {
                setPaymentReference(e.target.value);
                if (errors.paymentReference)
                  setErrors((prev) => ({ ...prev, paymentReference: "" }));
              }}
              fullWidth
              required={needsPaymentReference}
              disabled={!needsPaymentReference}
              error={!!errors.paymentReference}
              helperText={
                errors.paymentReference ||
                (!needsPaymentReference ? "Not applicable for " + lender : "")
              }
            />
          </>
        )}

        <Divider className={classes.divider} />

        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Mortgage Details
        </Typography>

        <TextField
          className={classes.textField}
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            if (errors.startDate)
              setErrors((prev) => ({ ...prev, startDate: "" }));
          }}
          fullWidth
          required
          InputLabelProps={{ shrink: true }}
          error={!!errors.startDate}
          helperText={errors.startDate}
        />

        <TextField
          className={classes.textField}
          label="Current Balance Amount (£)"
          type="number"
          value={currentBalanceAmount}
          onChange={(e) => {
            setCurrentBalanceAmount(e.target.value);
            if (errors.currentBalanceAmount)
              setErrors((prev) => ({ ...prev, currentBalanceAmount: "" }));
          }}
          fullWidth
          required
          inputProps={{ min: 1, step: 1 }}
          onKeyDown={(e) => {
            if (e.key === "." || e.key === ",") e.preventDefault();
          }}
          error={!!errors.currentBalanceAmount}
          helperText={errors.currentBalanceAmount}
        />

        <TextField
          className={classes.textField}
          label="Remaining Term (months)"
          type="number"
          value={fixedPaymentCount}
          onChange={(e) => {
            setFixedPaymentCount(e.target.value);
            if (errors.fixedPaymentCount)
              setErrors((prev) => ({ ...prev, fixedPaymentCount: "" }));
          }}
          fullWidth
          required
          inputProps={{ min: 1, step: 1 }}
          onKeyDown={(e) => {
            if (e.key === "." || e.key === ",") e.preventDefault();
          }}
          error={!!errors.fixedPaymentCount}
          helperText={errors.fixedPaymentCount}
        />

        <TextField
          className={classes.textField}
          label="Monthly Payment Amount (£)"
          type="number"
          value={fixedPaymentAmount}
          onChange={(e) => {
            setFixedPaymentAmount(e.target.value);
            if (errors.fixedPaymentAmount)
              setErrors((prev) => ({ ...prev, fixedPaymentAmount: "" }));
          }}
          fullWidth
          required
          inputProps={{ min: 1, step: 1 }}
          onKeyDown={(e) => {
            if (e.key === "." || e.key === ",") e.preventDefault();
          }}
          error={!!errors.fixedPaymentAmount}
          helperText={errors.fixedPaymentAmount}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isJointMortgage}
              onChange={(e) => setIsJointMortgage(e.target.checked)}
              color="primary"
            />
          }
          label="Joint Mortgage"
          style={{ marginTop: "1em" }}
        />

        <CardActions className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitting}
            fullWidth
          >
            {submitting && (
              <CircularProgress
                size={20}
                thickness={2}
                style={{ marginRight: "0.5em" }}
              />
            )}
            Submit Mortgage Update
          </Button>
        </CardActions>

        {successMessage && (
          <div className={classes.successBox}>{successMessage}</div>
        )}
      </Paper>
      <Notification />
    </div>
  );
};

export default HybridMortgageUpdate;
